/**
 * Rutas de operations — operativa: CRM, contactos, inventario, productos y proyectos.
 *
 * Se componen en `src/pages/index.jsx`. Es un fragmento, no un array: React Router
 * recorre los `<React.Fragment>` hijos de `<Routes>`, así que el JSX se mudó tal cual
 * —sin `key` ni comas— y el diff es el movimiento, nada más.
 *
 * `routeTable.test.jsx` congela las 174 rutas de la app contra la tabla previa a la
 * mudanza: perder una ruta aquí no da error, da un 404.
 */
import { Route, Navigate } from 'react-router-dom';
import { lazyWithRetry } from '@/lib/lazyWithRetry';
import { PermissionGuard } from '@/components/auth/PermissionGuard';
import { PERMS } from '@/constants/permissions';

const CRMPipeline = lazyWithRetry(() => import("@/pages/CRMPipeline"));
const Contactos = lazyWithRetry(() => import("@/pages/Contactos"));
const Productos = lazyWithRetry(() => import("@/pages/Productos"));
const RentabilidadHub = lazyWithRetry(() => import("@/pages/RentabilidadHub"));
const ProjectsHub = lazyWithRetry(() => import("@/pages/ProjectsHub"));
const ProjectDetail = lazyWithRetry(() => import("@/pages/ProjectDetail"));
const MyTasks = lazyWithRetry(() => import("@/pages/MyTasks"));
const Recursos = lazyWithRetry(() => import("@/pages/Recursos"));

export const routes = (
  <>

                {/* CRM — hub único compacto pipeline-first (feat/crm-compact-hub). Todo el
                    CRM vive en /CRMPipeline con tabs ?tab=pipeline|actividades|segmentacion;
                    Actividades tiene sub-tabs en ?sub=. Las páginas standalone previas
                    (Actividades, Segmentación, Conversaciones, Secuencias) redirigen aquí. */}
                <Route path="/CRM" element={<Navigate to="/CRMPipeline" replace />} />

                <Route path="/crm" element={<Navigate to="/CRMPipeline" replace />} />

                <Route path="/crm-actividades" element={<Navigate to="/CRMPipeline?tab=actividades" replace />} />

                <Route path="/CRMActividades" element={<Navigate to="/CRMPipeline?tab=actividades" replace />} />

                <Route path="/CRMConversaciones" element={<Navigate to="/CRMPipeline?tab=actividades" replace />} />

                <Route path="/CRMEmailIntegracion" element={<Navigate to="/configuracion?section=integraciones" replace />} />

                <Route path="/crm-pipeline" element={<PermissionGuard permission={PERMS.CRM_VIEW} fallback={<Navigate to="/" replace />}><CRMPipeline /></PermissionGuard>} />

                {/* Secuencias fusionada como sub-tab de Actividades dentro del hub */}
                <Route path="/crm-secuencias" element={<Navigate to="/CRMPipeline?tab=actividades&sub=secuencias" replace />} />

                <Route path="/crm-segmentacion" element={<Navigate to="/CRMPipeline?tab=segmentacion" replace />} />

                {/* Inventory */}
                <Route path="/InventarioStock" element={<Navigate to="/productos?tab=stock" replace />} />

                <Route path="/InventarioMovimientos" element={<Navigate to="/productos?tab=movimientos" replace />} />

                <Route path="/InventarioPedidos" element={<Navigate to="/productos?tab=pedidos" replace />} />

                {/* Pipeline — canónica /crm-pipeline es hyphenated; añade render PascalCase + legacy */}
                <Route path="/CRMPipeline" element={<PermissionGuard permission={PERMS.CRM_VIEW} fallback={<Navigate to="/" replace />}><CRMPipeline /></PermissionGuard>} />

                <Route path="/CRMLeads" element={<Navigate to="/CRMPipeline?tab=leads" replace />} />

                <Route path="/contactos" element={<Contactos />} />


                {/* Inventario — backend gatea products/purchase-orders/stock con inventory.view */}
                <Route path="/inventario" element={<PermissionGuard permission={PERMS.INVENTORY_VIEW} fallback={<Navigate to="/" replace />}><Navigate to="/Productos" replace /></PermissionGuard>} />

                <Route path="/inventario-movimientos" element={<PermissionGuard permission={PERMS.INVENTORY_VIEW} fallback={<Navigate to="/" replace />}><Navigate to="/Productos?tab=movimientos" replace /></PermissionGuard>} />

                <Route path="/inventario-pedidos" element={<PermissionGuard permission={PERMS.INVENTORY_VIEW} fallback={<Navigate to="/" replace />}><Navigate to="/Productos?tab=pedidos" replace /></PermissionGuard>} />

                <Route path="/inventario-stock" element={<PermissionGuard permission={PERMS.INVENTORY_VIEW} fallback={<Navigate to="/" replace />}><Navigate to="/Productos?tab=stock" replace /></PermissionGuard>} />

                {/* Recuento físico (VEN-4c) fusionado como tab de /productos.
                    El path legacy se conserva y redirige; el hub gatea la tab
                    por INVENTORY_EDIT (no-edit cae a la tab por defecto). */}
                <Route path="/inventario-recuento" element={<Navigate to="/productos?tab=recuento" replace />} />

                <Route path="/productos" element={<PermissionGuard permission={PERMS.INVENTORY_VIEW} fallback={<Navigate to="/" replace />}><Productos /></PermissionGuard>} />


                {/* Proyectos */}
                <Route path="/proyectos" element={<Navigate to="/projects" replace />} />

                <Route path="/project-detail-page" element={<Navigate to="/project-detail" replace />} />

                {/* Rentabilidad fusion — /rentabilidad-proyectos is now the
                    Hub (Profitability + Project billing + Investment as tabs).
                    Old routes redirect to ?tab= so bookmarks keep working. */}
                <Route path="/rentabilidad-proyectos" element={<RentabilidadHub />} />

                <Route path="/project-billing-overview" element={<Navigate to="/facturas?tab=projects" replace />} />

                {/* PR4-bis #9 — /projects is now the Hub (Lista + Kanban global
                    como tabs). /global-kanban se mantiene como redirect para
                    bookmarks. La página Projects original sigue importada
                    porque ProjectsHub la carga lazy en el tab Lista. */}
                <Route path="/projects" element={<ProjectsHub />} />

                <Route path="/global-kanban" element={<Navigate to="/projects?tab=kanban" replace />} />

                <Route path="/project-detail" element={<ProjectDetail />} />

                <Route path="/my-tasks" element={<MyTasks />} />

                <Route path="/recursos" element={<Recursos />} />


                {/* Inversiones — folded into Rentabilidad hub */}
                <Route path="/proyecto-inversion" element={<Navigate to="/rentabilidad-proyectos?tab=investment" replace />} />
  </>
);
